import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { Avatar, Tooltip } from 'antd'
import {
  AlertOutlined,
  AppstoreOutlined,
  BankOutlined,
  BarChartOutlined,
  ClusterOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  HomeOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  ShopOutlined,
  UserOutlined,
  VideoCameraOutlined,
  WifiOutlined,
} from '@ant-design/icons'
import { useAuth } from '@/app/AuthProvider'
import { useDesign } from '@/app/DesignProvider'
import {
  NAV_GROUP_LABELS,
  NAV_GROUP_ORDER,
  NAV_ITEMS,
  type NavGroup,
  type NavItem,
} from '@/shared/permissions'
import { CalendarModal } from '@/features/calendar/CalendarModal'
import { RemoteDesktopHost } from '@/features/remote/RemoteDesktopHost'
import { FtpClientHost } from '@/features/ftp/FtpClientHost'
import { ChatHost, useChat } from '@/features/chat/ChatHost'
import { UpdatesHost, useUpdates } from '@/features/notifications/UpdatesHost'
import { TopBar } from './TopBar'
import { StorePicker } from './StorePicker'
import styles from './MainLayout.module.css'

const NAV_ICONS: Record<string, ReactNode> = {
  home: <HomeOutlined />,
  cash: <ShopOutlined />,
  'pos-incidents': <AlertOutlined />,
  'piot-setup': <ClusterOutlined />,
  scales: <AppstoreOutlined />,
  'ofd-sbis': <FileTextOutlined />,
  'integration-journal': <FileProtectOutlined />,
  'chz-journal': <SafetyCertificateOutlined />,
  certificates: <BankOutlined />,
  mtswifi: <WifiOutlined />,
  trassir: <VideoCameraOutlined />,
  statistics: <BarChartOutlined />,
  settings: <SettingOutlined />,
}

function groupItems(items: NavItem[]): { group: NavGroup; label: string; items: NavItem[] }[] {
  return NAV_GROUP_ORDER.map((group) => ({
    group,
    label: NAV_GROUP_LABELS[group],
    items: items.filter((i) => i.group === group),
  })).filter((g) => g.items.length > 0)
}

function ShellChrome({ children }: { children: ReactNode }) {
  const { user, hasPermission } = useAuth()
  const { sidebarCollapsed, setSidebarCollapsed } = useDesign()
  const { setOpen: setChatOpen, unread: chatUnread } = useChat()
  const { setOpen: setUpdatesOpen, unread: updatesUnread } = useUpdates()
  const location = useLocation()
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [storePickerOpen, setStorePickerOpen] = useState(false)

  const menuItems = useMemo(
    () =>
      NAV_ITEMS.filter((item) => {
        if (item.adminOnly && user?.id !== 1) return false
        return hasPermission(item.permission)
      }),
    [hasPermission, user?.id],
  )

  const groups = useMemo(() => groupItems(menuItems), [menuItems])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // code=KeyK — работает и на русской раскладке; key="k" там не приходит
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return
      if (e.code !== 'KeyK' && e.key.toLowerCase() !== 'k') return
      e.preventDefault()
      e.stopPropagation()
      setStorePickerOpen(true)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches) {
      setSidebarCollapsed(true)
    }
  }, [location.pathname, setSidebarCollapsed])

  const isActive = (item: NavItem) =>
    item.path === '/'
      ? location.pathname === '/'
      : location.pathname === item.path || location.pathname.startsWith(item.path + '/')

  return (
    <div className={`${styles.shell} ${sidebarCollapsed ? styles.shellCollapsed : ''}`}>
      {!sidebarCollapsed ? (
        <button
          type="button"
          className={styles.backdrop}
          aria-label="Закрыть меню"
          onClick={() => setSidebarCollapsed(true)}
        />
      ) : null}
      <aside className={styles.sidebar} aria-label="Главное меню">
        <div className={styles.sidebarHead}>
          <div className={styles.brandBlock}>
            <span className={styles.brandMark}>K</span>
            {!sidebarCollapsed ? <span className={styles.brand}>KIT Support</span> : null}
          </div>
        </div>

        <nav className={styles.nav}>
          {groups.map((g) => (
            <div key={g.group} className={styles.navGroup}>
              {g.label && !sidebarCollapsed ? (
                <div className={styles.navGroupLabel}>{g.label}</div>
              ) : null}
              {g.items.map((item) => {
                const active = isActive(item)
                const link = (
                  <NavLink
                    to={item.path}
                    className={`${styles.navItem} ${active ? styles.navItemActive : ''}`}
                    title={item.label}
                    aria-current={active ? 'page' : undefined}
                  >
                    <span className={styles.navIcon}>
                      {NAV_ICONS[item.key] || <AppstoreOutlined />}
                    </span>
                    {!sidebarCollapsed ? (
                      <span className={styles.navLabel}>{item.label}</span>
                    ) : null}
                  </NavLink>
                )
                return (
                  <div key={item.key}>
                    {sidebarCollapsed ? (
                      <Tooltip title={item.label} placement="right">
                        {link}
                      </Tooltip>
                    ) : (
                      link
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <div className={styles.userCard} title={user?.name || 'Пользователь'}>
            <Avatar
              size={28}
              src={user?.avatar_url || undefined}
              icon={<UserOutlined />}
              style={{
                background: 'var(--bg-hover)',
                color: 'var(--text-secondary)',
                flexShrink: 0,
              }}
            />
            {!sidebarCollapsed ? (
              <span className={styles.userMeta}>
                <span className={styles.userName}>{user?.name || 'Пользователь'}</span>
                <span className={styles.userRole}>{user?.position || 'Сотрудник'}</span>
              </span>
            ) : null}
          </div>
        </div>
      </aside>

      <div className={styles.column}>
        <TopBar
          onOpenStorePicker={() => setStorePickerOpen(true)}
          onOpenCalendar={() => setCalendarOpen(true)}
          onOpenChat={() => setChatOpen(true)}
          chatUnread={chatUnread}
          onOpenUpdates={() => setUpdatesOpen(true)}
          updatesUnread={updatesUnread}
        />
        <main className={styles.main}>{children}</main>
      </div>

      <StorePicker open={storePickerOpen} onClose={() => setStorePickerOpen(false)} />
      <CalendarModal open={calendarOpen} onClose={() => setCalendarOpen(false)} />
    </div>
  )
}

export function MainLayout() {
  return (
    <RemoteDesktopHost>
      <FtpClientHost>
        <ChatHost>
          <UpdatesHost>
            <ShellChrome>
              <Outlet />
            </ShellChrome>
          </UpdatesHost>
        </ChatHost>
      </FtpClientHost>
    </RemoteDesktopHost>
  )
}
